import CustomLink from "@Components/CustomLink";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import styles from "./HundredsOfCities.module.scss";
import { TOP_CITIES, TOP_COUNTRIES } from "./data";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import wrapperHOC from "@Utils/wrapperHOC";
import useIsLanguage from "hooks/useIsLanguage";

const splitItemsIntoRows = (items: any, rowCount = 2) => {
  const rows = [];
  const perRow = Math.ceil(items.length / rowCount);
  for (let i = 0; i < items.length; i += perRow) {
    rows.push(items.slice(i, i + perRow));
  }
  return rows;
};

const HundredsContent = ({
  isDesktop,
  activeTab,
  customCities = null,
  customCountries = null,
  showTabs = true,
}: {
  isDesktop: boolean;
  activeTab: "cities" | "countries";
  customCities?: Array<{ name: string; link: string }> | null;
  customCountries?: Array<{ name: string; link: string }> | null;
  showTabs?: boolean;
}) => {
  const userLanguage = useIsLanguage();

  const handleCityClick = (name: string) => {
    gtagEventEmitter({
      action: "clicked_city_of_hundred_cities",
      category: "HOME",
      canonical_name: name,
    });
  };

  // Use custom lists if provided, otherwise use default lists
  const cities = customCities || TOP_CITIES;
  const countries = customCountries || TOP_COUNTRIES;

  const cityRows = splitItemsIntoRows(cities, 2);
  const countryRows = isDesktop ? [countries] : splitItemsIntoRows(countries, 2);

  // If tabs are disabled and we have a custom list, render that list directly
  if (!showTabs && (customCities || customCountries)) {
    const itemsToShow = customCities || customCountries;
    const rows = isDesktop ? [itemsToShow] : splitItemsIntoRows(itemsToShow, 2);

    return (
      <div className={formatClassNames(styles.tabContentWrapper, !isDesktop && styles.mobile)}>
        <div className={formatClassNames(styles.tabPane, styles.visibleTab)}>
          <div className={styles.scrollerContent}>
            {rows.map((row, rowIndex) => (
              <div key={`custom-row-${rowIndex}`} className={styles.contentRow}>
                {row.map((item: any) => (
                  <CustomLink
                    to={`/places/search/${item.link}`}
                    role="none"
                    className={styles.cityLink}
                    onClick={() => handleCityClick(item.name)}
                    data-testid="custom-item"
                    key={item.name}
                  >
                    {item?.name}
                  </CustomLink>
                ))}
              </div>
            ))}
          </div>
        </div>
        <div className={styles.rightBlur} />
      </div>
    );
  }

  return (
    <div className={styles.tabContentWrapper}>
      <div
        className={formatClassNames(
          styles.tabPane,
          styles.citiesWrapper,
          activeTab === "cities" ? styles.visibleTab : styles.hiddenTab,
        )}
      >
        <div className={styles.scrollerContent}>
          {cityRows.map((row, rowIndex) => (
            <div key={`city-row-${rowIndex}`} className={styles.contentRow}>
              {row.map((city: any) => (
                <CustomLink
                  to={`/places/search/${city.link}`}
                  role="none"
                  className={styles.cityLink}
                  onClick={() => handleCityClick(city.name)}
                  data-testid="citynames-hundred-of-cities"
                  key={city.name}
                >
                  {`${userLanguage === "zh" ? city.name_zh : city.name}`}
                </CustomLink>
              ))}
            </div>
          ))}
        </div>
      </div>

      <div className={styles.rightBlur} />

      <div
        className={formatClassNames(
          styles.tabPane,
          styles.countriesWrapper,
          activeTab === "countries" ? styles.visibleTab : styles.hiddenTab,
        )}
      >
        <div className={styles.scrollerContent}>
          {countryRows.map((row, rowIndex) => (
            <div key={`country-row-${rowIndex}`} className={styles.contentRow}>
              {row.map((country: any) => (
                <CustomLink
                  to={`/places/search/${country.link}`}
                  role="none"
                  className={styles.cityLink}
                  key={country.name}
                >
                  {`${userLanguage === "zh" ? country.name_zh : country.name}`}
                </CustomLink>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default wrapperHOC(HundredsContent, {
  componentName: "HundredsContent-Home",
  showForChina: true,
  showForMiniProgram: false,
});
